import React, { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  Autocomplete,
  IconButton,
  Stack,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Chip,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  Business as BusinessIcon,
  LocalShipping as TruckIcon,
  Description as DescriptionIcon,
  Calculate as CalculateIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { T } from "../../../theme";
import {
  partyList,
  customBillCreate,
  customBillUpdate,
  customBillDetail,
} from "../../../Services/Services";

// ── Style constants ────────────────────────────────────────────────
const sq = { borderRadius: "4px !important" };

const primaryBtn = {
  ...sq,
  bgcolor: T.primary,
  color: T.white,
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryDark,
    boxShadow: "0 2px 8px rgba(79,70,229,0.3)",
  },
  "&.Mui-disabled": { bgcolor: `${T.primary}50`, color: T.white },
};

const ghostBtn = {
  ...sq,
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  color: T.primary,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryBg,
    borderColor: T.borderHover,
    boxShadow: "none",
  },
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    ...sq,
    bgcolor: T.white,
    fontSize: "0.8rem",
    "& fieldset": { border: `1.5px solid ${T.border}` },
    "&:hover fieldset": { border: `1.5px solid ${T.borderHover}` },
    "&.Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
  },
  "& .MuiInputLabel-root": { fontSize: "0.78rem" },
  "& .MuiInputLabel-root.Mui-focused": { color: T.primary },
};

const tableInputSx = {
  "& .MuiOutlinedInput-root": {
    ...sq,
    height: 30,
    bgcolor: T.white,
    fontSize: "0.72rem",
    "& fieldset": { border: `1.5px solid ${T.border}` },
    "&:hover fieldset": { border: `1.5px solid ${T.borderHover}` },
    "&.Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
  },
  "& input": { padding: "4px 8px" },
};

// ── Utility Helpers ────────────────────────────────────────────────
const extractPartyId = (val) => {
  if (val && typeof val === "object" && val.party_id) return val.party_id;
  return null;
};

const extractPartyText = (val) => {
  if (typeof val === "string") return val;
  return "";
};

// ── Section header ─────────────────────────────────────────────────
const SectionHeader = ({ icon: Icon, title, color = T.primary }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      mb: 1.2,
      pb: 0.75,
      borderBottom: `1.5px dashed ${T.border}`,
    }}
  >
    <Box
      sx={{
        width: 24,
        height: 24,
        ...sq,
        bgcolor: `${color}18`,
        border: `1px solid ${color}40`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon sx={{ fontSize: 14, color }} />
    </Box>
    <Typography
      sx={{
        fontSize: "0.78rem",
        fontWeight: 800,
        color,
        letterSpacing: "0.02em",
        textTransform: "uppercase",
      }}
    >
      {title}
    </Typography>
  </Box>
);

// ── Party Selector Component ──────────────────────────────────────
const PartySelector = ({ label, value, onChange, options = [] }) => {
  const isSelected = value && typeof value === "object" && value?.party_id;

  return (
    <Box
      sx={{
        p: 1.25,
        ...sq,
        bgcolor: T.white,
        border: `1px solid ${T.border}`,
        height: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 800,
            color: T.text,
            textTransform: "uppercase",
          }}
        >
          {label}
        </Typography>
        {isSelected && (
          <Chip
            label="Saved Party"
            size="small"
            sx={{
              ...sq,
              height: 16,
              bgcolor: T.primaryBg,
              color: T.primary,
              border: `1px solid ${T.primary}40`,
              fontSize: "0.55rem",
              fontWeight: 700,
              "& .MuiChip-label": { px: 0.6 },
            }}
          />
        )}
      </Box>

      <Autocomplete
        freeSolo
        size="small"
        options={options}
        groupBy={(option) =>
          option?.party_type === "consignee"
            ? "Consignee"
            : option?.party_type === "broker"
              ? "Broker"
              : "Consignor"
        }
        getOptionLabel={(o) => {
          if (typeof o === "string") return o;
          return o?.name || "";
        }}
        value={value || null}
        onChange={(_, newValue) => onChange(newValue)}
        onInputChange={(_, newInputValue, reason) => {
          if (reason === "input") onChange(newInputValue);
          if (reason === "clear") onChange("");
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.party_id}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "100%",
                py: 0.25,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  color: T.text,
                  flex: 1,
                }}
              >
                {option.name}
              </Typography>
              {option.party_type && (
                <Chip
                  label={
                    option.party_type === "consignee"
                      ? "Consignee"
                      : option.party_type === "broker"
                        ? "Broker"
                        : "Consignor"
                  }
                  size="small"
                  sx={{
                    ...sq,
                    height: 16,
                    fontSize: "0.55rem",
                    fontWeight: 700,
                    bgcolor:
                      option.party_type === "consignee"
                        ? "#fef3c7"
                        : option.party_type === "broker"
                          ? "#f0fdf4"
                          : "#e0e7ff",
                    color:
                      option.party_type === "consignee"
                        ? "#92400e"
                        : option.party_type === "broker"
                          ? "#15803d"
                          : "#3730a3",
                    "& .MuiChip-label": { px: 0.5 },
                  }}
                />
              )}
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Select from list or type new..."
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                ...sq,
                height: 44,
                bgcolor: T.white,
                fontSize: "0.8rem",
                padding: "0 8px !important",
                "& fieldset": { border: `1.5px solid ${T.border}` },
                "&:hover fieldset": { border: `1.5px solid ${T.borderHover}` },
                "&.Mui-focused fieldset": {
                  border: `1.5px solid ${T.primary}`,
                },
              },
              "& .MuiAutocomplete-input": {
                padding: "4px 4px !important",
                fontSize: "0.8rem",
              },
            }}
          />
        )}
      />
    </Box>
  );
};

// ── Blank item row ─────────────────────────────────────────────────
const blankItem = () => ({
  id: Date.now() + Math.random(),
  description: "",
  hsn: "",
  quantity: "",
  unit: "TON",
  rate: "",
  per: "TON",
  amount: "",
});

// ── Formatter ──────────────────────────────────────────────────────
const formatCurrency = (a) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(a || 0);

// ── Mobile Item Card ───────────────────────────────────────────────
const MobileItemCard = ({ item, idx, onChange, onRemove, disableRemove }) => (
  <Paper
    elevation={0}
    sx={{
      ...sq,
      border: `1.5px solid ${T.border}`,
      p: 1.25,
      mb: 1,
      bgcolor: T.white,
    }}
  >
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 1.25,
        pb: 0.75,
        borderBottom: `1px dashed ${T.border}`,
      }}
    >
      <Chip
        label={`Item #${idx + 1}`}
        size="small"
        sx={{
          ...sq,
          height: 20,
          bgcolor: T.primaryBg,
          color: T.primary,
          border: `1px solid ${T.primary}40`,
          fontSize: "0.65rem",
          fontWeight: 800,
          "& .MuiChip-label": { px: 0.9 },
        }}
      />
      <Tooltip title="Remove" arrow>
        <span>
          <IconButton
            size="small"
            disabled={disableRemove}
            onClick={() => onRemove(item.id)}
            sx={{
              width: 26,
              height: 26,
              ...sq,
              color: T.red,
              bgcolor: T.redBg,
              border: `1px solid ${T.redBorder}`,
              "&:hover": {
                bgcolor: "#fee2e2",
                borderColor: T.red,
              },
              "&.Mui-disabled": {
                bgcolor: "#f3f4f6",
                color: T.textMuted,
                borderColor: T.border,
              },
            }}
          >
            <DeleteIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>

    <Grid container spacing={1}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          size="small"
          label="Description of Goods"
          value={item.description}
          onChange={(e) => onChange(item.id, "description", e.target.value)}
          placeholder="e.g. TMT Bar 25MM"
          sx={inputSx}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          size="small"
          label="HSN/SAC"
          value={item.hsn}
          onChange={(e) => onChange(item.id, "hsn", e.target.value)}
          placeholder="72142090"
          sx={inputSx}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          size="small"
          label="Unit"
          value={item.unit}
          onChange={(e) => onChange(item.id, "unit", e.target.value)}
          placeholder="TON"
          sx={inputSx}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          size="small"
          type="number"
          label="Quantity"
          value={item.quantity}
          onChange={(e) => onChange(item.id, "quantity", e.target.value)}
          placeholder="0.00"
          sx={inputSx}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          size="small"
          type="number"
          label="Rate"
          value={item.rate}
          onChange={(e) => onChange(item.id, "rate", e.target.value)}
          placeholder="0.00"
          sx={inputSx}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          size="small"
          label="per"
          value={item.per}
          onChange={(e) => onChange(item.id, "per", e.target.value)}
          placeholder="TON"
          sx={inputSx}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          size="small"
          type="number"
          label="Amount"
          value={item.amount}
          onChange={(e) => onChange(item.id, "amount", e.target.value)}
          placeholder="0.00"
          sx={{
            ...inputSx,
            "& input": { fontWeight: 700, color: T.primary },
          }}
        />
      </Grid>
    </Grid>
  </Paper>
);

// ═══════════════════════════════════════════════════════════════════
// ── Main Component ─────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════
const CustomBillCreation = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const navigate = useNavigate();
  const location = useLocation();
  const { id: editId } = useParams();

  const isEditMode = Boolean(editId); // sirf URL id se detect
  const [editData, setEditData] = useState(location.state?.editData || null);
  const [loadingData, setLoadingData] = useState(false);

  // All parties combined (consignor + consignee + broker)
  const [partyOptions, setPartyOptions] = useState([]);
  // Party state (single field)
  const [selectedParty, setSelectedParty] = useState(null);

  // Header
  const [headerDetails, setHeaderDetails] = useState({
    invoice_no: "",
    eway_bill_no: "",
    invoice_date: "",
    delivery_note: "",
    payment_terms: "",
    reference_no: "",
    reference_date: "",
    other_references: "",
    buyer_order_no: "",
    buyer_order_date: "",
    dispatch_doc_no: "",
    delivery_note_date: "",
    dispatched_through: "",
    destination: "",
    bill_of_lading: "",
    motor_vehicle_no: "",
    terms_of_delivery: "",
  });

  const [items, setItems] = useState([blankItem()]);
  const [cgstPercent, setCgstPercent] = useState("");
  const [sgstPercent, setSgstPercent] = useState("");

  // Save state
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Fetch parties
  useEffect(() => {
    const fetchParties = async () => {
      try {
        const [consignors, consignees, brokers] = await Promise.all([
          partyList({ party_type: "consignor" }),
          partyList({ party_type: "consignee" }),
          partyList({ party_type: "broker" }),
        ]);

        // Tag each party with its type + merge into single list
        const tagged = [
          ...(Array.isArray(consignors) ? consignors : []).map((p) => ({
            ...p,
            party_type: "consignor",
          })),
          ...(Array.isArray(consignees) ? consignees : []).map((p) => ({
            ...p,
            party_type: "consignee",
          })),
          ...(Array.isArray(brokers) ? brokers : []).map((p) => ({
            ...p,
            party_type: "broker",
          })),
        ];

        setPartyOptions(tagged);
      } catch (error) {
        console.error("Failed to fetch parties:", error);
        setPartyOptions([]);
      }
    };
    fetchParties();
  }, []);

  useEffect(() => {
    if (!isEditMode || !editId) return;

    // Agar navigation state se already data hai to skip
    if (editData) return;

    const fetchBillData = async () => {
      setLoadingData(true);
      try {
        const data = await customBillDetail(editId);
        if (data) {
          setEditData(data);
        } else {
          setSnackbar({
            open: true,
            message: "Bill not found",
            severity: "error",
          });
          setTimeout(() => navigate("/dashboard/custom-bill-list"), 1500);
        }
      } catch (err) {
        console.error("Failed to fetch bill:", err);
        setSnackbar({
          open: true,
          message: "Failed to load bill data",
          severity: "error",
        });
      } finally {
        setLoadingData(false);
      }
    };

    fetchBillData();
  }, [isEditMode, editId]);

  // ── Pre-fill form for edit mode ──
  useEffect(() => {
    if (!isEditMode || !editData) return;

    // Party pre-fill helper
    const findParty = (options, partyId, textFallback) => {
      if (partyId) {
        const found = options.find((p) => p.party_id === partyId);
        if (found) return found;
      }
      return textFallback || null;
    };

    // Party pre-fill (only when options are ready)
    if (partyOptions.length > 0) {
      setSelectedParty(
        findParty(partyOptions, editData.party_id, editData.party_text),
      );
    }
    // Header fields
    setHeaderDetails({
      invoice_no: editData.invoice_no || "",
      eway_bill_no: editData.eway_bill_no || "",
      invoice_date: editData.invoice_date || "",
      delivery_note: editData.delivery_note || "",
      payment_terms: editData.payment_terms || "",
      reference_no: editData.reference_no || "",
      reference_date: editData.reference_date || "",
      other_references: editData.other_references || "",
      buyer_order_no: editData.buyer_order_no || "",
      buyer_order_date: editData.buyer_order_date || "",
      dispatch_doc_no: editData.dispatch_doc_no || "",
      delivery_note_date: editData.delivery_note_date || "",
      dispatched_through: editData.dispatched_through || "",
      destination: editData.destination || "",
      bill_of_lading: editData.bill_of_lading || "",
      motor_vehicle_no: editData.motor_vehicle_no || "",
      terms_of_delivery: editData.terms_of_delivery || "",
    });

    // Items pre-fill
    const existingItems = editData.custom_items || [];
    if (existingItems.length > 0) {
      setItems(
        existingItems.map((it) => ({
          id: it.item_id || Date.now() + Math.random(),
          description: it.description || "",
          hsn: it.hsn || "",
          quantity: it.quantity || "",
          unit: it.unit || "TON",
          rate: it.rate || "",
          per: it.per || "TON",
          amount: it.amount || "",
        })),
      );
    } else {
      setItems([blankItem()]);
    }

    // Tax fields
    setCgstPercent(editData.cgst_percent || "");
    setSgstPercent(editData.sgst_percent || "");
  }, [isEditMode, editData, partyOptions]);

  // Handlers
  const handleHeaderChange = (field, value) =>
    setHeaderDetails((p) => ({ ...p, [field]: value }));

  const handleItemChange = (id, field, value) =>
    setItems((prev) =>
      prev.map((it) => {
        if (it.id !== id) return it;
        const updated = { ...it, [field]: value };
        if (field === "quantity" || field === "rate") {
          const q =
            parseFloat(field === "quantity" ? value : updated.quantity) || 0;
          const r = parseFloat(field === "rate" ? value : updated.rate) || 0;
          updated.amount = (q * r).toFixed(2);
        }
        return updated;
      }),
    );

  const addItem = () => setItems((p) => [...p, blankItem()]);
  const removeItem = (id) =>
    setItems((p) => (p.length === 1 ? p : p.filter((it) => it.id !== id)));

  // Calculations
  const totalTaxableValue = useMemo(
    () => items.reduce((sum, it) => sum + (parseFloat(it.amount) || 0), 0),
    [items],
  );
  const cgstAmount = useMemo(() => {
    const pct = parseFloat(cgstPercent) || 0;
    return (totalTaxableValue * pct) / 100;
  }, [totalTaxableValue, cgstPercent]);
  const sgstAmount = useMemo(() => {
    const pct = parseFloat(sgstPercent) || 0;
    return (totalTaxableValue * pct) / 100;
  }, [totalTaxableValue, sgstPercent]);
  const grandTotal = useMemo(
    () => totalTaxableValue + cgstAmount + sgstAmount,
    [totalTaxableValue, cgstAmount, sgstAmount],
  );

  // ── Reset Form ──
  const handleReset = () => {
    setSelectedParty(null);
    setHeaderDetails({
      invoice_no: "",
      eway_bill_no: "",
      invoice_date: "",
      delivery_note: "",
      payment_terms: "",
      reference_no: "",
      reference_date: "",
      other_references: "",
      buyer_order_no: "",
      buyer_order_date: "",
      dispatch_doc_no: "",
      delivery_note_date: "",
      dispatched_through: "",
      destination: "",
      bill_of_lading: "",
      motor_vehicle_no: "",
      terms_of_delivery: "",
    });
    setItems([blankItem()]);
    setCgstPercent("");
    setSgstPercent("");
  };

  // ── Save Handler ──
  const handleSave = async () => {
    if (!selectedParty) {
      setSnackbar({
        open: true,
        message: "Party is required",
        severity: "error",
      });
      return;
    }

    const validItems = items.filter(
      (it) => it.description?.trim() || it.quantity || it.rate || it.amount,
    );

    if (validItems.length === 0) {
      setSnackbar({
        open: true,
        message: "At least 1 item is required",
        severity: "error",
      });
      return;
    }

    setSaving(true);

    try {
      const payload = {
        party_id: extractPartyId(selectedParty),
        party_text: extractPartyText(selectedParty),
        ...headerDetails,
        total_taxable_value: parseFloat(totalTaxableValue.toFixed(2)),
        cgst_percent: parseFloat(cgstPercent) || 0,
        cgst_amount: parseFloat(cgstAmount.toFixed(2)),
        sgst_percent: parseFloat(sgstPercent) || 0,
        sgst_amount: parseFloat(sgstAmount.toFixed(2)),
        grand_total: parseFloat(grandTotal.toFixed(2)),
        total_amount: parseFloat(grandTotal.toFixed(2)),
        rem_amount: parseFloat(grandTotal.toFixed(2)),
        paid_amount: isEditMode ? editData.paid_amount || 0 : 0,
        items: validItems.map((it) => ({
          description: it.description || "",
          hsn: it.hsn || "",
          quantity: parseFloat(it.quantity) || 0,
          unit: it.unit || "TON",
          rate: parseFloat(it.rate) || 0,
          per: it.per || "TON",
          amount: parseFloat(it.amount) || 0,
        })),
      };

      const dateFields = [
        "invoice_date",
        "reference_date",
        "buyer_order_date",
        "delivery_note_date",
      ];
      dateFields.forEach((f) => {
        if (!payload[f]) payload[f] = null;
      });

      let response;

      if (isEditMode) {
        // ── UPDATE ──
        response = await customBillUpdate(editId, payload);
        if (response?.billing_id) {
          setSnackbar({
            open: true,
            message: `Custom bill #${response.billing_id} updated successfully!`,
            severity: "success",
          });
          setTimeout(() => navigate("/dashboard/custom-bill-list"), 1000);
        }
      } else {
        // ── CREATE ──
        response = await customBillCreate(payload);
        if (response?.billing_id) {
          setSnackbar({
            open: true,
            message: `Custom bill #${response.billing_id} created successfully!`,
            severity: "success",
          });
          handleReset();
          setTimeout(() => {
            navigate("/dashboard/custom-bill-list");
          }, 1000);
        }
      }
    } catch (err) {
      const data = err.response?.data;
      let errorMsg = isEditMode
        ? "Failed to update custom bill"
        : "Failed to create custom bill";
      if (data) {
        if (typeof data === "string") errorMsg = data;
        else if (data.detail) errorMsg = data.detail;
        else errorMsg = JSON.stringify(data);
      }
      setSnackbar({ open: true, message: errorMsg, severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (isEditMode && loadingData) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          gap: 2,
        }}
      >
        <CircularProgress sx={{ color: T.primary }} size={40} />
        <Typography
          sx={{ fontSize: "0.85rem", color: T.textSub, fontWeight: 600 }}
        >
          Loading bill data...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        px: { xs: 0.5, sm: 0 },
        pt: 0,
        mt: { xs: 1, md: -2 },
        maxWidth: 1400,
        mx: "auto",
        width: "100%",
        pb: 4,
      }}
    >
      {/* ── Header ── */}
      <Paper
        elevation={0}
        sx={{
          ...sq,
          border: `1px solid ${T.border}`,
          overflow: "hidden",
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: 1.5,
            bgcolor: "#f8fafc",
            borderBottom: `1px solid ${T.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            {/* Back button in edit mode */}
            {isEditMode && (
              <IconButton
                size="small"
                onClick={() => navigate(-1)}
                sx={{
                  ...sq,
                  bgcolor: T.white,
                  border: `1.5px solid ${T.border}`,
                  color: T.primary,
                  "&:hover": { bgcolor: T.primaryBg },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}

            <Box
              sx={{
                width: 32,
                height: 32,
                ...sq,
                bgcolor: T.white,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ReceiptIcon sx={{ color: T.primary, fontSize: 17 }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: "#111827",
                  fontSize: { xs: "0.9em", sm: "1em" },
                  lineHeight: 1.2,
                }}
              >
                {isEditMode
                  ? `Edit Custom Bill — #${editId}`
                  : "Custom Bill Creation"}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.6rem", sm: "0.68rem" },
                  color: T.textSub,
                }}
              >
                {isEditMode
                  ? `Editing bill for ${editData?.party_name || editData?.party_text || ""}`
                  : "Manually create a tax invoice with full details"}
              </Typography>
            </Box>
          </Stack>

          {isEditMode && (
            <Chip
              label="Edit Mode"
              size="small"
              sx={{
                ...sq,
                height: 22,
                bgcolor: "#fef3c7",
                color: "#92400e",
                border: "1px solid #fbbf24",
                fontSize: "0.62rem",
                fontWeight: 700,
              }}
            />
          )}
        </Box>
      </Paper>

      {/* ── PARTY DETAILS ── */}
      <Paper
        elevation={0}
        sx={{
          ...sq,
          border: `1px solid ${T.border}`,
          overflow: "hidden",
          mb: 1.5,
          p: { xs: 1.25, sm: 2 },
        }}
      >
        <SectionHeader icon={BusinessIcon} title="Party Details" />
        <Grid container spacing={{ xs: 1.25, md: 2 }}>
          <Grid item xs={12} md={6}>
            <PartySelector
              label="Party Name"
              value={selectedParty}
              onChange={setSelectedParty}
              options={partyOptions}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* ── INVOICE & TRANSPORT DETAILS ── */}
      <Paper
        elevation={0}
        sx={{
          ...sq,
          border: `1px solid ${T.border}`,
          overflow: "hidden",
          mb: 1.5,
          p: { xs: 1.25, sm: 2 },
        }}
      >
        <SectionHeader icon={DescriptionIcon} title="Invoice & Order Details" />
        <Grid container spacing={{ xs: 1.25, sm: 1.5 }}>
          {[
            { field: "invoice_no", label: "Invoice No." },
            { field: "eway_bill_no", label: "e-Way Bill No." },
            { field: "invoice_date", label: "Dated", type: "date" },
            { field: "delivery_note", label: "Delivery Note" },
            { field: "payment_terms", label: "Mode/Terms of Payment" },
            { field: "reference_no", label: "Reference No." },
            { field: "reference_date", label: "Reference Date", type: "date" },
            { field: "other_references", label: "Other References" },
            { field: "buyer_order_no", label: "Buyer's Order No." },
            { field: "buyer_order_date", label: "Order Date", type: "date" },
            { field: "dispatch_doc_no", label: "Dispatch Doc No." },
            {
              field: "delivery_note_date",
              label: "Delivery Note Date",
              type: "date",
            },
          ].map(({ field, label, type }) => (
            <Grid item xs={12} sm={6} md={3} key={field}>
              <TextField
                fullWidth
                size="small"
                label={label}
                type={type || "text"}
                value={headerDetails[field]}
                onChange={(e) => handleHeaderChange(field, e.target.value)}
                InputLabelProps={type === "date" ? { shrink: true } : {}}
                sx={inputSx}
              />
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px dashed ${T.border}` }}>
          <SectionHeader
            icon={TruckIcon}
            title="Dispatch & Delivery"
            color="#0891b2"
          />
          <Grid container spacing={{ xs: 1.25, sm: 1.5 }}>
            {[
              { field: "dispatched_through", label: "Dispatched Through" },
              { field: "destination", label: "Destination" },
              { field: "bill_of_lading", label: "Bill of Lading/LR-RR No." },
              { field: "motor_vehicle_no", label: "Motor Vehicle No." },
            ].map(({ field, label }) => (
              <Grid item xs={12} sm={6} md={3} key={field}>
                <TextField
                  fullWidth
                  size="small"
                  label={label}
                  value={headerDetails[field]}
                  onChange={(e) => handleHeaderChange(field, e.target.value)}
                  sx={inputSx}
                />
              </Grid>
            ))}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Terms of Delivery"
                multiline
                minRows={2}
                value={headerDetails.terms_of_delivery}
                onChange={(e) =>
                  handleHeaderChange("terms_of_delivery", e.target.value)
                }
                sx={inputSx}
              />
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* ── ITEMS ── */}
      <Paper
        elevation={0}
        sx={{
          ...sq,
          border: `1px solid ${T.border}`,
          overflow: "hidden",
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: 1.25,
            bgcolor: "#f8fafc",
            borderBottom: `1px solid ${T.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
          }}
        >
          <SectionHeader
            icon={DescriptionIcon}
            title="Item Details"
            color={T.primary}
          />
          <Button
            size="small"
            onClick={addItem}
            startIcon={<AddIcon sx={{ fontSize: 15 }} />}
            sx={{ ...primaryBtn, height: 30, px: 1.5, flexShrink: 0 }}
          >
            Add {isMobile ? "" : "Item"}
          </Button>
        </Box>

        {isMobile ? (
          <Box sx={{ p: 1.25 }}>
            {items.map((item, idx) => (
              <MobileItemCard
                key={item.id}
                item={item}
                idx={idx}
                onChange={handleItemChange}
                onRemove={removeItem}
                disableRemove={items.length === 1}
              />
            ))}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 1.5,
                py: 1,
                ...sq,
                bgcolor: `${T.primary}12`,
                border: `1.5px solid ${T.primary}`,
                mt: 0.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 800,
                  color: T.primary,
                  textTransform: "uppercase",
                }}
              >
                Total Taxable
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.85rem",
                  fontWeight: 800,
                  color: T.primary,
                }}
              >
                {formatCurrency(totalTaxableValue)}
              </Typography>
            </Box>
          </Box>
        ) : (
          <TableContainer>
            <Table
              size="small"
              sx={{
                "& td, & th": {
                  py: 0.5,
                  px: 0.75,
                  fontSize: "0.72rem",
                  borderBottom: `1px solid #e5e7eb`,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                },
                "& th": {
                  fontWeight: 700,
                  color: T.headerText,
                  bgcolor: `${T.headerBg} !important`,
                  fontSize: "0.72rem",
                  letterSpacing: "0.02em",
                  height: 32,
                },
                "& td": { height: 40 },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 40 }}>Sl No.</TableCell>
                  <TableCell sx={{ minWidth: 260 }}>
                    Description of Goods
                  </TableCell>
                  <TableCell sx={{ minWidth: 100 }}>HSN/SAC</TableCell>
                  <TableCell sx={{ minWidth: 110 }}>Quantity</TableCell>
                  <TableCell sx={{ minWidth: 80 }}>Unit</TableCell>
                  <TableCell sx={{ minWidth: 110 }}>Rate</TableCell>
                  <TableCell sx={{ minWidth: 70 }}>per</TableCell>
                  <TableCell sx={{ minWidth: 130 }}>Amount</TableCell>
                  <TableCell sx={{ minWidth: 50 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, idx) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: T.textMuted,
                          fontSize: "0.72rem",
                        }}
                      >
                        {idx + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        value={item.description}
                        onChange={(e) =>
                          handleItemChange(
                            item.id,
                            "description",
                            e.target.value,
                          )
                        }
                        placeholder="e.g. TMT Bar 25MM"
                        sx={tableInputSx}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        value={item.hsn}
                        onChange={(e) =>
                          handleItemChange(item.id, "hsn", e.target.value)
                        }
                        placeholder="72142090"
                        sx={tableInputSx}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        type="number"
                        value={item.quantity}
                        onChange={(e) =>
                          handleItemChange(item.id, "quantity", e.target.value)
                        }
                        placeholder="0.00"
                        sx={tableInputSx}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        value={item.unit}
                        onChange={(e) =>
                          handleItemChange(item.id, "unit", e.target.value)
                        }
                        placeholder="TON"
                        sx={tableInputSx}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        type="number"
                        value={item.rate}
                        onChange={(e) =>
                          handleItemChange(item.id, "rate", e.target.value)
                        }
                        placeholder="0.00"
                        sx={tableInputSx}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        value={item.per}
                        onChange={(e) =>
                          handleItemChange(item.id, "per", e.target.value)
                        }
                        placeholder="TON"
                        sx={tableInputSx}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        type="number"
                        value={item.amount}
                        onChange={(e) =>
                          handleItemChange(item.id, "amount", e.target.value)
                        }
                        placeholder="0.00"
                        sx={{
                          ...tableInputSx,
                          "& input": {
                            padding: "4px 8px",
                            fontWeight: 700,
                            color: T.primary,
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Remove" arrow>
                        <span>
                          <IconButton
                            size="small"
                            disabled={items.length === 1}
                            onClick={() => removeItem(item.id)}
                            sx={{
                              width: 24,
                              height: 24,
                              ...sq,
                              color: T.red,
                              bgcolor: T.redBg,
                              border: `1px solid ${T.redBorder}`,
                              "&:hover": {
                                bgcolor: "#fee2e2",
                                borderColor: T.red,
                              },
                              "&.Mui-disabled": {
                                bgcolor: "#f3f4f6",
                                color: T.textMuted,
                                borderColor: T.border,
                              },
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 13 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow
                  sx={{
                    "& td": {
                      bgcolor: `${T.primary}12 !important`,
                      fontWeight: 800,
                      borderTop: `2px solid ${T.primary}`,
                    },
                  }}
                >
                  <TableCell colSpan={7} align="right">
                    <Typography
                      sx={{
                        fontSize: "0.78rem",
                        fontWeight: 800,
                        color: T.primary,
                        pr: 1,
                      }}
                    >
                      TOTAL TAXABLE VALUE
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        fontSize: "0.85rem",
                        fontWeight: 800,
                        color: T.primary,
                      }}
                    >
                      {formatCurrency(totalTaxableValue)}
                    </Typography>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ── TAX SUMMARY ── */}
      <Paper
        elevation={0}
        sx={{
          ...sq,
          border: `1px solid ${T.border}`,
          overflow: "hidden",
          mb: 1.5,
          p: { xs: 1.25, sm: 2 },
          display: "flex",
          justifyContent: { xs: "stretch", md: "flex-end" },
        }}
      >
        <Box sx={{ width: { xs: "100%", md: 480 } }}>
          <SectionHeader
            icon={CalculateIcon}
            title="Tax Summary"
            color="#0891b2"
          />
          <Stack spacing={0.75}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 1.5,
                py: 0.85,
                ...sq,
                bgcolor: `${T.primary}0a`,
                border: `1px solid ${T.primary}30`,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  color: T.primary,
                }}
              >
                Total Taxable Value
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.85rem",
                  fontWeight: 800,
                  color: T.primary,
                }}
              >
                {formatCurrency(totalTaxableValue)}
              </Typography>
            </Box>

            {/* CGST */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr 70px 110px",
                  sm: "1fr 90px 140px",
                },
                gap: 1,
                alignItems: "center",
                px: 1.5,
                py: 0.75,
                ...sq,
                bgcolor: "#fef3c7",
                border: "1px solid #fcd34d",
              }}
            >
              <Typography
                sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#92400e" }}
              >
                CGST
              </Typography>
              <TextField
                size="small"
                type="number"
                value={cgstPercent}
                onChange={(e) => setCgstPercent(e.target.value)}
                placeholder="0"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography sx={{ fontSize: "0.7rem", color: "#92400e" }}>
                        %
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    ...sq,
                    height: 30,
                    bgcolor: "#fff",
                    fontSize: "0.72rem",
                    "& fieldset": { border: "1.5px solid #fcd34d" },
                    "&:hover fieldset": { border: "1.5px solid #f59e0b" },
                    "&.Mui-focused fieldset": { border: "1.5px solid #d97706" },
                  },
                  "& input": { padding: "4px 6px" },
                }}
                inputProps={{ min: 0, max: 100, step: 0.5 }}
              />
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: 800,
                  color: "#92400e",
                  textAlign: "right",
                }}
              >
                {formatCurrency(cgstAmount)}
              </Typography>
            </Box>

            {/* SGST */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr 70px 110px",
                  sm: "1fr 90px 140px",
                },
                gap: 1,
                alignItems: "center",
                px: 1.5,
                py: 0.75,
                ...sq,
                bgcolor: "#fef3c7",
                border: "1px solid #fcd34d",
              }}
            >
              <Typography
                sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#92400e" }}
              >
                SGST/UTGST
              </Typography>
              <TextField
                size="small"
                type="number"
                value={sgstPercent}
                onChange={(e) => setSgstPercent(e.target.value)}
                placeholder="0"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography sx={{ fontSize: "0.7rem", color: "#92400e" }}>
                        %
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    ...sq,
                    height: 30,
                    bgcolor: "#fff",
                    fontSize: "0.72rem",
                    "& fieldset": { border: "1.5px solid #fcd34d" },
                    "&:hover fieldset": { border: "1.5px solid #f59e0b" },
                    "&.Mui-focused fieldset": { border: "1.5px solid #d97706" },
                  },
                  "& input": { padding: "4px 6px" },
                }}
                inputProps={{ min: 0, max: 100, step: 0.5 }}
              />
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: 800,
                  color: "#92400e",
                  textAlign: "right",
                }}
              >
                {formatCurrency(sgstAmount)}
              </Typography>
            </Box>

            {/* Grand Total */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 1.5,
                py: 1.15,
                ...sq,
                bgcolor: "#dcfce7",
                border: "2px solid #16a34a",
                mt: 0.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "0.78rem", sm: "0.85rem" },
                  fontWeight: 800,
                  color: "#15803d",
                  textTransform: "uppercase",
                  letterSpacing: "0.03em",
                }}
              >
                Grand Total
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  fontWeight: 900,
                  color: "#15803d",
                }}
              >
                {formatCurrency(grandTotal)}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Paper>

      {/* ── Footer Actions ── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: { xs: "stretch", sm: "flex-end" },
          gap: 1,
          mt: 2,
          flexDirection: { xs: "column-reverse", sm: "row" },
        }}
      >
        <Button
          onClick={isEditMode ? () => navigate(-1) : handleReset}
          disabled={saving}
          sx={{
            ...ghostBtn,
            height: 40,
            px: 2.5,
            width: { xs: "100%", sm: "auto" },
          }}
        >
          {isEditMode ? "Cancel" : "Reset"}
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          startIcon={
            saving ? (
              <CircularProgress size={14} sx={{ color: T.white }} />
            ) : (
              <ReceiptIcon sx={{ fontSize: 16 }} />
            )
          }
          sx={{
            ...primaryBtn,
            height: 40,
            px: 3,
            width: { xs: "100%", sm: "auto" },
          }}
        >
          {saving
            ? isEditMode
              ? "Updating..."
              : "Saving..."
            : isEditMode
              ? "Update Bill"
              : "Save Bill"}
        </Button>
      </Box>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ top: "80px !important" }}
      >
        <Alert
          severity={snackbar.severity}
          sx={{ ...sq, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomBillCreation;
